// Persona presentation: activity badges and the agent card used on the World
// rail and the Cast grid.
import { Link } from 'wouter';
import type { Activity } from '../../convex/shared/validators';
import type { LiveSessionView, PersonaCard } from '../../convex/shared/views';
import { fmtRelative, humanize, shortName } from '../lib/format';
import { Avatar } from '../ui/Avatar';
import type { IconName } from '../ui/Icon';
import { Chip, Meter } from '../ui/primitives';
import { LiveScreen } from './LiveScreen';

export const ACTIVITY: Record<Activity, { label: string; icon: IconName; tone: 'sky' | 'sun' | 'leaf' | 'coral' | 'grape' | 'neutral' }> = {
  asleep: { label: 'Asleep', icon: 'zzz', tone: 'grape' },
  off_duty: { label: 'Off duty', icon: 'moon', tone: 'neutral' },
  commuting: { label: 'Commuting', icon: 'truck', tone: 'sky' },
  working: { label: 'Working', icon: 'wrench', tone: 'leaf' },
  driving: { label: 'Driving', icon: 'wheel', tone: 'sun' },
  using_app: { label: 'Using aa-app', icon: 'desktop', tone: 'coral' },
  in_meeting: { label: 'In a meeting', icon: 'people', tone: 'grape' },
  on_break: { label: 'On a break', icon: 'sun', tone: 'neutral' },
};

const APP_STATUS: Record<PersonaCard['aaAppStatus'], string> = {
  not_invited: 'No aa-app seat yet',
  invited: 'Invited to aa-app',
  active: 'aa-app seat active',
  locked_out: 'Locked out of aa-app',
};

export function ActivityChip({ activity }: { activity: Activity }) {
  const a = ACTIVITY[activity];
  return (
    <Chip tone={a.tone} icon={a.icon}>
      {a.label}
    </Chip>
  );
}

export function PersonaAvatar({ card, size = 48, live }: { card: PersonaCard; size?: number; live?: boolean }) {
  return (
    <Avatar
      personaKey={card.key}
      mood={card.mood}
      fatigue={card.fatigue}
      stress={card.stress}
      asleep={card.activity === 'asleep'}
      size={size}
      ring={live ? 'var(--c-coral)' : undefined}
    />
  );
}

export function PersonaCardView({ card, session, now, variant = 'rail' }: { card: PersonaCard; session?: LiveSessionView; now: number; variant?: 'rail' | 'grid' }) {
  const live = session && (session.status === 'active' || session.status === 'starting');
  return (
    <Link href={`/agent/${card.key}`} className={`persona-card is-${variant} ${card.enabled ? '' : 'is-disabled'}`}>
      <div className="persona-top">
        <PersonaAvatar card={card} size={variant === 'grid' ? 56 : 44} live={Boolean(live)} />
        <div className="persona-id">
          <strong>{variant === 'grid' ? card.name : shortName(card.name)}</strong>
          <span className="persona-title">{card.title}</span>
          <ActivityChip activity={card.activity} />
        </div>
        {live ? <span className="live-badge">Live</span> : null}
      </div>
      {card.currentPlan ? <p className="persona-plan">{card.currentPlan}</p> : null}
      <div className="persona-meters">
        <Meter compact label="Mood" value={(card.mood + 1) / 2} tone="sun" />
        <Meter compact label="Energy" value={card.energy} tone="leaf" />
        <Meter compact label="Stress" value={card.stress} tone="coral" />
      </div>
      {variant === 'grid' && session ? (
        <div className="persona-screen">
          <LiveScreen session={session} />
        </div>
      ) : null}
      <div className="persona-foot">
        <span title={APP_STATUS[card.aaAppStatus]}>{humanize(card.aaAppStatus)}</span>
        <span>{card.nextWakeAt ? `Next move ${fmtRelative(card.nextWakeAt, now)}` : card.enabled ? 'Waiting' : 'Benched'}</span>
      </div>
    </Link>
  );
}
